<script setup lang="ts">
  import { watch, ref } from 'vue'

  import { useImage } from '@/composables/useImage'
  import { IbexaImageVariations } from '@/enums/IbexaImageVariations'
  import { VgsDrawerColorScheme } from '@/enums/VgsDrawerColorScheme'
  import type { Author } from '@/types/Author'

  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Typography from '@/components/common/Typography.vue'
  import VgsButton from '@/components/common/VgsButton.vue'
  import VgsDrawer from '@/components/common/VgsDrawer.vue'

  const props = withDefaults(defineProps<{
    authors: Author[]
    prefix?: string
    updatedAt?: string
    variant?: 'vertical' | 'horizontal' | 'text-only'
    noLines?: boolean
  }>(), {
    prefix: '',
    updatedAt: '',
    variant: 'vertical',
    noLines: false
  })

  const { getImageByField } = useImage()

  const selectedAuthor = ref<Author | null>(null)
  const imageSrc = ref<string>('')

  const isSecondToLastAuthor = (index: number) => index === props.authors.length - 2
  const isLastAuthor = (index: number) => index === props.authors.length - 1

  watch(() => selectedAuthor.value, async () => {
    if (!selectedAuthor?.value?.image?.destinationContentId) return
    imageSrc.value = await getImageByField(selectedAuthor.value.image.destinationContentId, 'image', IbexaImageVariations.Article780)
  })
</script>

<template>
  <VgsDrawer
    :open="!!selectedAuthor"
    :title="''"
    :color-scheme="VgsDrawerColorScheme.Orange"
    @close="selectedAuthor = null"
  >
    <img
      v-if="imageSrc"
      class="author-image"
      :src="imageSrc"
      aria-hidden="true"
    >
    <Typography
      component="h2"
      variant="h4"
    >
      {{ `${selectedAuthor?.firstname ?? ''} ${selectedAuthor?.lastname ?? ''}` }}
    </Typography>
    <RichTextRenderer :rich-text="selectedAuthor?.description ?? ''" />
  </VgsDrawer>
  <div
    class="byline-container"
    :class="props.variant"
  >
    <div
      v-if="props.variant === 'text-only'"
      class="byline"
      :class="[props.variant, { 'no-lines': noLines }]"
    >
      <div
        class="byline-text"
        :class="props.variant"
      >
        <p class="byline-title">
          <Typography variant="caption">
            {{ `${prefix} ` }}
          </Typography>
          <template
            v-for="(author, i) in props.authors"
            :key="author.name"
          >
            <VgsButton
              variant="link"
              @click="selectedAuthor = author"
            >
              {{ `${author.firstname} ${author.lastname}` }}
            </VgsButton>
            <template v-if="isSecondToLastAuthor(i)">
              og
            </template>
            <template v-else-if="!isLastAuthor(i)">
              {{ ", " }}
            </template>
          </template>
        </p>
        <p v-if="!!props.updatedAt">
          Sist oppdatert: {{ props.updatedAt }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  $border-byline: 1px solid;
  $color-horizontal: var(--color-pink-30);

  * {
    margin: 0;
  }

  .author-image {
    max-width: 100%;
    margin-bottom: 24px;
  }

  .byline-container {
    display: flex;

    &.text-only {
      flex-wrap: wrap;
    }
  }

  // Shame
  .vgs-button {
    padding: 8px 0 2px;
  }

  .byline {
    max-width: 140px;
    width: fit-content;
    display: flex;
    gap: 3px;
    align-items: center;

    &.vertical {
      padding: 0 9px;
      flex-direction: column;
      border-left: $border-byline;
      border-right: $border-byline;
      border-color: var(--color-violet-50);

      &:not(:first-child) {
        border-left: none;
      }
    }

    &.horizontal {
      max-width: none;
      gap: 9px;
      padding: 16px 10px;
      border-top: $border-byline;
      border-bottom: $border-byline;
      border-color: $color-horizontal;
      flex-shrink: 0;

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        border-right: none;
        padding-right: 32px;
      }

      &::after {
        content: '';
        width: 1px;
        height: 100%;
        background-color: $color-horizontal;
      }

      &:last-child::after {
        display: none;
      }
    }

    &.text-only {
      padding: 32px 0;
      max-width: none;
      border-top: $border-byline;
      border-bottom: $border-byline;
      border-color: var(--color-gray-20);

      .separator {
        display: none;
      }
    }

    &.no-lines.text-only {
      border-color: transparent;
      padding: 0;
    }
  }

  .byline-image {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }

    &.text-only {
      display: none;
    }
  }

  .byline-text {
    max-width: 107px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    font-weight: 500;
    font-size: .875rem;

    &:last-child {
      border-right: none;
    }

    .byline-title {
      line-height: 171.429%; /* 24px */
    }

    .byline-subtitle {
      color: var(--app-theme-color);
      text-align: center;
      font-size: .75rem;
      font-style: normal;
      font-weight: 400;
      line-height: 125%; /* 0.9375rem */
    }

    &.horizontal {
      align-items: flex-start;
      padding: 12px 0;

      .byline-subtitle {
        text-align: left;
      }
    }

    &.text-only {
      max-width: none;
      font-weight: 400;
      align-items: flex-start;
    }
  }
</style>
